import React from 'react';
import { Link } from 'react-router-dom';
import { FiChevronRight, FiFileText } from 'react-icons/fi';
import { useSettings } from '../context/SettingsContext';

const Section = ({ title, children }) => (
  <div className="mb-8">
    <h2 className="text-lg font-bold text-gray-800 mb-3 flex items-center gap-2">
      <span className="w-1 h-5 bg-primary-600 rounded-full inline-block" />
      {title}
    </h2>
    <div className="text-gray-600 text-sm leading-relaxed space-y-2">{children}</div>
  </div>
);

const TermsOfServicePage = () => {
  const { settings } = useSettings();
  const siteName = settings.siteName || 'Hospitex';

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="bg-primary-800 text-white py-10">
        <div className="container-custom">
          <nav className="flex items-center gap-1.5 text-sm text-primary-300 mb-4">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <FiChevronRight size={12} />
            <span className="text-white">Terms of Service</span>
          </nav>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center">
              <FiFileText size={22} />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-extrabold">Terms of Service</h1>
              <p className="text-primary-200 text-sm mt-0.5">Last updated: {new Date().toLocaleDateString('en-PK', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="container-custom py-10">
        <div className="max-w-3xl mx-auto bg-white rounded-2xl border border-gray-100 shadow-sm p-8">
          <p className="text-gray-600 text-sm leading-relaxed mb-8">
            By accessing or using {siteName} ("the Site"), you agree to be bound by these Terms of Service. Please read them carefully before using our services.
          </p>

          <Section title="Use of the Site">
            <p>You agree to use this Site only for lawful purposes. You must not:</p>
            <ul className="list-disc pl-5 space-y-1 mt-2">
              <li>Use the site for any fraudulent or unlawful purpose.</li>
              <li>Attempt to gain unauthorized access to any part of the site.</li>
              <li>Transmit harmful, offensive, or disruptive content.</li>
              <li>Reproduce or copy our content without written permission.</li>
            </ul>
          </Section>

          <Section title="Product Information">
            <p>We strive to display accurate product descriptions, images, and pricing. However:</p>
            <ul className="list-disc pl-5 space-y-1 mt-2">
              <li>Product images are for illustrative purposes and may slightly differ from actual items.</li>
              <li>Specifications are subject to change by manufacturers without notice.</li>
              <li>Prices may be updated without prior notice.</li>
              <li>We reserve the right to limit quantities or refuse orders at our discretion.</li>
            </ul>
          </Section>

          <Section title="Ordering & Payment">
            <ul className="list-disc pl-5 space-y-1">
              <li>Orders are subject to availability and acceptance.</li>
              <li>Payment is accepted via Cash on Delivery (COD) or bank transfer.</li>
              <li>Prices are displayed in Pakistani Rupees (PKR) inclusive of applicable taxes.</li>
              <li>We reserve the right to cancel orders in case of pricing errors.</li>
            </ul>
          </Section>

          <Section title="Shipping & Delivery">
            <ul className="list-disc pl-5 space-y-1">
              <li>Delivery times are estimates and not guaranteed.</li>
              <li>Free shipping applies to orders above the threshold shown on our website.</li>
              <li>Risk of damage passes to the buyer upon delivery.</li>
              <li>Inspect all items upon delivery and report damage within 24 hours.</li>
            </ul>
          </Section>

          <Section title="Returns & Refunds">
            <p>We accept returns under the following conditions:</p>
            <ul className="list-disc pl-5 space-y-1 mt-2">
              <li>Item must be returned within 7 days of delivery.</li>
              <li>Item must be unused, in original packaging with all accessories.</li>
              <li>Defective items will be repaired or replaced at our discretion.</li>
              <li>Custom or special-order items are non-refundable.</li>
              <li>Refunds will be processed within 7–14 business days.</li>
            </ul>
          </Section>

          <Section title="Intellectual Property">
            <p>All content on this website including logos, images, text, and software is the property of {siteName} and protected by applicable intellectual property laws. Unauthorized use is strictly prohibited.</p>
          </Section>

          <Section title="Limitation of Liability">
            <p>{siteName} shall not be liable for any indirect, incidental, or consequential damages arising from use of our products or website. Our maximum liability is limited to the amount paid for the specific product or service in question.</p>
          </Section>

          <Section title="Governing Law">
            <p>These Terms are governed by the laws of Pakistan. Any disputes shall be subject to the exclusive jurisdiction of the courts of Lahore, Punjab, Pakistan.</p>
          </Section>

          <Section title="Changes to Terms">
            <p>We reserve the right to modify these Terms at any time. Continued use of the Site following changes constitutes acceptance. We encourage you to review these Terms periodically.</p>
          </Section>

          <Section title="Contact">
            <p>For questions regarding these Terms, contact us at:</p>
            <div className="mt-3 bg-gray-50 rounded-xl p-4 space-y-1">
              <p><strong>{siteName}</strong></p>
              {settings.address && <p>{settings.address}</p>}
              {settings.email && <p>Email: <a href={`mailto:${settings.email}`} className="text-primary-600">{settings.email}</a></p>}
              {settings.phone && <p>Phone: {settings.phone}</p>}
            </div>
          </Section>

          <div className="border-t border-gray-100 pt-6 flex flex-wrap gap-4 text-sm">
            <Link to="/privacy" className="text-primary-600 hover:underline font-medium">Privacy Policy</Link>
            <Link to="/contact" className="text-primary-600 hover:underline font-medium">Contact Us</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfServicePage;
